

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react';
import { getAccessToken, isTokenExpired, refreshAccessToken } from './utils/auth';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { loginTheme, dashboardLightTheme, dashboardDarkTheme } from './theme';

// Lazy load route components
const Login = lazy(() => import('./components/Login'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const RefreshToken = lazy(() => import('./components/RefreshToken'));
const Unauthorized = lazy(() => import('./components/Unauthorized'));

// Regular imports for components used on every route
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Loading fallback for lazy-loaded routes
const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  // Default to dark and persist the choice. Previously this started 'light' and only
  // became the user's theme once Dashboard's setMode ran at the END of its data fetch —
  // so if that fetch hiccuped post-login the app was stuck on a light theme that hid the
  // tiles. A stable stored default decouples the theme from the fetch.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('galaxy_theme_mode') || 'dark'; } catch { return 'dark'; }
  });
  useEffect(() => { try { if (mode) localStorage.setItem('galaxy_theme_mode', mode); } catch {} }, [mode]);
  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  const dashboardTheme = useMemo(() => (mode === 'light' ? dashboardLightTheme : dashboardDarkTheme),[mode]);

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = getAccessToken();
      if (token && isTokenExpired(token)) {
        await refreshAccessToken();
      }
    };

    checkAndRefreshToken();
    const intervalId = setInterval(checkAndRefreshToken, 4 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <ErrorBoundary fullPage errorTitle="Application Error" errorMessage="The application encountered an unexpected error.">
      <Router>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/refresh" element={
              <ErrorBoundary fullPage errorTitle="Refresh Token Error" errorMessage="Unable to load Refresh Token module.">
                <RefreshToken />
              </ErrorBoundary>
            } />
            <Route
              path="/dashboard"
              element={
                <ThemeProvider theme={dashboardTheme}>
                  <CssBaseline />
                  <ProtectedRoute>
                    <ErrorBoundary fullPage errorTitle="Dashboard Error" errorMessage="Unable to load Dashboard module.">
                      <Dashboard toggleMode={toggleMode} mode={mode} setMode={setMode}/>
                    </ErrorBoundary>
                  </ProtectedRoute>
                </ThemeProvider>
              }
            />
            <Route path='/unauthorized' element={
              <ErrorBoundary fullPage errorTitle="Unauthorized Error" errorMessage="Unable to load Unauthorized module.">
                <Unauthorized />
              </ErrorBoundary>
            } />
            <Route path="/" element={
              <ThemeProvider theme={loginTheme}>
                <CssBaseline />
                <ErrorBoundary fullPage errorTitle="Login Error" errorMessage="Unable to load Login module.">
                  <Login />
                </ErrorBoundary>
              </ThemeProvider>} />
            <Route path="*" element={
              <ThemeProvider theme={loginTheme}>
                <CssBaseline />
                <ErrorBoundary fullPage errorTitle="Login Error" errorMessage="Unable to load Login module.">
                  <Login />
                </ErrorBoundary>
              </ThemeProvider>} />
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
